import { useCallback } from 'react';
import { DispatchReviewDto, LineItemDto, OrderByType, OrderLineItemDto, OrderReviewDto, OrderReviewLineItemDto, QuoteDto, MaterialLineItemDto } from '../dtos';
import { useCreateBillingRatesLineItemMutation } from '../store/generated/generatedApi';
import { emptyGuid, LineItemTypes } from './constants';

export function useLineItemConverter(quote?: QuoteDto) {
    const [getBillingRates] = useCreateBillingRatesLineItemMutation();

    const getDriverMemoForOrderLineItem = useCallback((quoteLineItem: LineItemDto): string => {
        const isMaterialLineItem = quoteLineItem.type === LineItemTypes.Material;

        if (isMaterialLineItem) {
            const materialLineItem = quoteLineItem as MaterialLineItemDto;
            if (materialLineItem.isCustomDriverMemo) {
                return materialLineItem.driverMemo ?? '';
            }
            else {
                return quoteLineItem.associatedLineItem?.driverMemo ?? '';
            }
        }

        return quoteLineItem.driverMemo ?? '';
    }, []);

    const getMaterialTaxableForOrderLineItem = useCallback((quoteLineItem: LineItemDto): boolean => {
        const isMaterialLineItem = quoteLineItem.type === LineItemTypes.Material;

        if (isMaterialLineItem) {
            const materialLineItem = quoteLineItem as MaterialLineItemDto & { isMaterialTaxable?: boolean };
            return materialLineItem.isMaterialTaxable ?? false;
        }

        return false;
    }, []);

    const getHaulFromAddress = useCallback(
        (lineItem: LineItemDto) => {
            switch (lineItem.type) {
                case LineItemTypes.Hourly:
                case LineItemTypes.Dump:
                case LineItemTypes.Misc: {
                    return {
                        addressLine1: quote?.addressLine1 ?? '',
                        addressLine2: quote?.addressLine2 ?? '',
                        city: quote?.city ?? '',
                        state: quote?.state ?? '',
                        zipCode: quote?.zipCode ?? '',
                        country: quote?.country ?? '',
                        directions: quote?.directions ?? '',
                        region: quote?.region ?? undefined,
                        regionId: quote?.region?.id ?? undefined,
                        siteName: quote?.siteName ?? '',
                        isHaulFromAddressLocked: true,
                    };
                }
                case LineItemTypes.Material: {
                    const hasMaterialHaulFromData =
                        !!(lineItem.haulFromAddressLine1?.trim()) ||
                        !!(lineItem.haulFromAddressLine2?.trim()) ||
                        !!(lineItem.haulFromCity?.trim()) ||
                        !!(lineItem.haulFromState?.trim()) ||
                        !!(lineItem.haulFromZipCode?.trim()) ||
                        !!(lineItem.haulFromCountry?.trim()) ||
                        !!(lineItem.haulFromDirections?.trim()) ||
                        !!(lineItem.haulFromSiteName?.trim()) ||
                        !!lineItem.haulFromRegionId ||
                        !!lineItem.haulFromRegion?.id;
                    const shouldUseAssociatedLineItem = !hasMaterialHaulFromData && !!lineItem.associatedLineItem;
                    const sourceLineItem = shouldUseAssociatedLineItem ? lineItem.associatedLineItem! : lineItem;
                    const sourceSite = shouldUseAssociatedLineItem ? lineItem.associatedLineItem?.site : lineItem.site;
                    return {
                        addressLine1: sourceLineItem.haulFromAddressLine1 ?? '',
                        addressLine2: sourceLineItem.haulFromAddressLine2 ?? '',
                        city: sourceLineItem.haulFromCity ?? '',
                        state: sourceLineItem.haulFromState ?? '',
                        zipCode: sourceLineItem.haulFromZipCode ?? '',
                        country: sourceLineItem.haulFromCountry ?? '',
                        directions: sourceLineItem.haulFromDirections ?? '',
                        region: sourceLineItem.haulFromRegion ?? undefined,
                        regionId: sourceLineItem.haulFromRegionId ?? sourceLineItem.haulFromRegion?.id ?? undefined,
                        siteName: sourceSite?.name ?? sourceLineItem.haulFromSiteName ?? '',
                        isHaulFromAddressLocked: true,
                    };
                }
                case LineItemTypes.SiteHauling: {
                    return {
                        addressLine1: lineItem.haulFromAddressLine1 ?? '',
                        addressLine2: lineItem.haulFromAddressLine2 ?? '',
                        city: lineItem.haulFromCity ?? '',
                        state: lineItem.haulFromState ?? '',
                        zipCode: lineItem.haulFromZipCode ?? '',
                        country: lineItem.haulFromCountry ?? '',
                        directions: lineItem.haulFromDirections ?? '',
                        region: lineItem.haulFromRegion ?? undefined,
                        regionId: lineItem.haulFromRegionId ?? lineItem.haulFromRegion?.id ?? undefined,
                        siteName: lineItem.haulFromSiteName ?? '',
                        isHaulFromAddressLocked: true,
                    };
                }
                default: {
                    return {
                        addressLine1: '',
                        addressLine2: '',
                        city: '',
                        state: '',
                        zipCode: '',
                        country: '',
                        directions: '',
                        region: undefined,
                        regionId: undefined,
                        siteName: '',
                        isHaulFromAddressLocked: false,
                    };
                }
            }
        },
        [quote]
    );

    const getHaulToAddress = useCallback(
        (lineItem: LineItemDto) => {
            switch (lineItem.type) {
                case LineItemTypes.Material:
                case LineItemTypes.SiteHauling: {
                    return {
                        addressLine1: quote?.addressLine1 ?? '',
                        addressLine2: quote?.addressLine2 ?? '',
                        city: quote?.city ?? '',
                        state: quote?.state ?? '',
                        zipCode: quote?.zipCode ?? '',
                        country: quote?.country ?? '',
                        directions: quote?.directions ?? '',
                        region: quote?.region ?? undefined,
                        regionId: quote?.region?.id ?? undefined,
                        siteName: quote?.siteName ?? '',
                    };
                }
                case LineItemTypes.Hourly: {
                    return {
                        addressLine1: lineItem.haulToAddressLine1 ?? '',
                        addressLine2: lineItem.haulToAddressLine2 ?? '',
                        city: lineItem.haulToCity ?? '',
                        state: lineItem.haulToState ?? '',
                        zipCode: lineItem.haulToZipCode ?? '',
                        country: lineItem.haulToCountry ?? '',
                        directions: lineItem.haulToDirections ?? '',
                        region: lineItem.haulToRegion ?? undefined,
                        regionId: lineItem.haulToRegionId ?? lineItem.haulToRegion?.id ?? undefined,
                        siteName: lineItem.site?.name ?? lineItem.haulToSiteName ?? '',
                    };
                }
                case LineItemTypes.Dump:
                case LineItemTypes.Misc: {
                    const site = lineItem.site;
                    return {
                        addressLine1: lineItem.haulToAddressLine1 ?? site?.addressLine1 ?? quote?.addressLine1 ?? '',
                        addressLine2: lineItem.haulToAddressLine2 ?? site?.addressLine2 ?? quote?.addressLine2 ?? '',
                        city: lineItem.haulToCity ?? site?.city ?? quote?.city ?? '',
                        state: lineItem.haulToState ?? site?.state ?? quote?.state ?? '',
                        zipCode: lineItem.haulToZipCode ?? site?.zipCode ?? quote?.zipCode ?? '',
                        country: lineItem.haulToCountry ?? site?.country ?? quote?.country ?? '',
                        directions: lineItem.haulToDirections ?? site?.directions ?? quote?.directions ?? '',
                        region: lineItem.haulToRegion ?? site?.region ?? quote?.region ?? undefined,
                        regionId:
                            lineItem.haulToRegionId ??
                            lineItem.haulToRegion?.id ??
                            site?.regionId ??
                            site?.region?.id ??
                            quote?.region?.id ??
                            undefined,
                        siteName: lineItem.haulToSiteName ?? site?.name ?? quote?.siteName ?? '',
                    };
                }
                default: {
                    return {
                        addressLine1: '',
                        addressLine2: '',
                        city: '',
                        state: '',
                        zipCode: '',
                        country: '',
                        directions: '',
                        region: undefined,
                        regionId: undefined,
                        siteName: '',
                    };
                }
            }
        },
        [quote]
    );

    const convertQuoteLineItemToOrderLineItem = useCallback(
        async (quoteLineItem: LineItemDto, newLineNumber: number): Promise<OrderLineItemDto> => {
            var haulToAddress = getHaulToAddress(quoteLineItem);
            var haulFromAddress = getHaulFromAddress(quoteLineItem);
            let billingRates = await getBillingRates(quoteLineItem).unwrap();
            
            const result: OrderLineItemDto = {
                id: emptyGuid,
                isActive: true,
                createdOn: new Date(),
                equipmentType: quoteLineItem.equipmentType,
                equipmentTypeId: quoteLineItem.equipmentTypeId,
                lineItemId: quoteLineItem.id,
                lineItem: quoteLineItem,
                orderBy: OrderByType.Quantity,
                loadOrQuantityReq: billingRates.quantity,
                equipmentRequired: 1,
                internalMemo: quoteLineItem.lineItemMemo ?? '',
                driverMemo: getDriverMemoForOrderLineItem(quoteLineItem),
                isMaterialTaxable: getMaterialTaxableForOrderLineItem(quoteLineItem),
                dispatches: [],
                yardTime: undefined,
                onsiteTime: undefined,
                dispatchingIntervalTime: undefined,
                orderLineItemNumber: newLineNumber,
                name: quoteLineItem.description,
                poNumber: quoteLineItem.isCustomPoNumber ? quoteLineItem.poNumber ?? quote?.poNumber ?? '' : quote?.poNumber ?? '',
                customerJobNumber: quoteLineItem.isCustomerJobNumberCustom
                    ? quoteLineItem.customerJobNumber ?? quote?.customerJobNumber ?? ''
                    : quote?.customerJobNumber ?? '',
                haulToAddressLine1: haulToAddress.addressLine1,
                haulToAddressLine2: haulToAddress.addressLine2,
                haulToCity: haulToAddress.city,
                haulToState: haulToAddress.state,
                haulToZipCode: haulToAddress.zipCode,
                haulToCountry: haulToAddress.country,
                haulToDirections: haulToAddress.directions,
                haulToRegion: haulToAddress.region,
                haulToRegionId: haulToAddress.regionId,
                siteId: quoteLineItem.siteId,
                haulToSiteName: haulToAddress.siteName,
                haulFromAddressLine1: haulFromAddress.addressLine1,
                haulFromAddressLine2: haulFromAddress.addressLine2,
                haulFromCity: haulFromAddress.city,
                haulFromState: haulFromAddress.state,
                haulFromZipCode: haulFromAddress.zipCode,
                haulFromCountry: haulFromAddress.country,
                haulFromDirections: haulFromAddress.directions,
                haulFromRegion: haulFromAddress.region,
                haulFromRegionId: haulFromAddress.regionId,
                haulFromSiteName: haulFromAddress.siteName,
                billingRates: billingRates,
                isCustomPoNumber: quoteLineItem.isCustomPoNumber,
                isCustomerJobNumberCustom: quoteLineItem.isCustomerJobNumberCustom
            };
            // Unique key for React when id is emptyGuid (multiple new items would otherwise share the same key)
            (result as OrderLineItemDto & { clientTempId?: string }).clientTempId = crypto.randomUUID();
            return result;
        }, [getBillingRates, getDriverMemoForOrderLineItem, getMaterialTaxableForOrderLineItem, getHaulFromAddress, getHaulToAddress, quote]);

    const convertQuoteLineItemToOrderReviewLineItem = useCallback(
        async (quoteLineItem: LineItemDto, newLineNumber: number, orderReview?: OrderReviewDto): Promise<OrderReviewLineItemDto> => {
            var orderLineItem = await convertQuoteLineItemToOrderLineItem(quoteLineItem, newLineNumber);

            return {
                ...orderLineItem,
                description: orderLineItem.name,
                dispatches: (orderLineItem.dispatches ?? []) as DispatchReviewDto[],
                isDispatched: (orderLineItem.dispatches?.length ?? 0) > 0,
                isInvoiced: false,
                isCancelable: orderLineItem.isCancelable ?? true,
                poNumber: orderLineItem.isCustomPoNumber ? orderLineItem.poNumber : orderReview?.poNumber ?? '',
                customerJobNumber: orderLineItem.isCustomerJobNumberCustom ? orderLineItem.customerJobNumber : orderReview?.customerJobNumber ?? '',
                siteId: orderLineItem.siteId,
                orderReviewId: orderReview?.id,
            };
        }, [convertQuoteLineItemToOrderLineItem]);

    const createNewOrderReviewLineItemFromQuoteLineItem = useCallback(
        async (quoteLineItem?: LineItemDto, orderReview?: OrderReviewDto): Promise<OrderReviewLineItemDto> => {
            if (quoteLineItem && orderReview) {
                return (await convertQuoteLineItemToOrderReviewLineItem(quoteLineItem, (orderReview?.orderLineItems?.length ?? 0) + 1, orderReview)) as OrderReviewLineItemDto;
            } else {
                return {
                    id: emptyGuid,
                    isActive: true,
                    createdOn: new Date(),
                    orderReviewId: orderReview?.id,
                    lineItemId: undefined,
                    equipmentTypeId: undefined,
                    description: '',
                    orderBy: OrderByType.Quantity,
                    loadOrQuantityReq: undefined,
                    equipmentRequired: undefined,
                    memo: '',
                    driverMemo: '',
                    dispatchMemo: '',
                    yardTime: undefined,
                    onsiteTime: undefined,
                    orderLineItemNumber: (orderReview?.orderLineItems?.length ?? 0) + 1,
                    dispatches: [],
                    haulToAddressLine1: '',
                    haulToAddressLine2: '',
                    haulToCity: '',
                    haulToState: '',
                    haulToZipCode: '',
                    haulToCountry: '',
                    haulToDirections: '',
                    haulFromAddressLine1: '',
                    haulFromAddressLine2: '',
                    haulFromCity: '',
                    haulFromState: '',
                    haulFromZipCode: '',
                    haulFromCountry: '',
                    haulFromDirections: '',
                    haulToRegionId: undefined,
                    haulToRegion: undefined,
                    haulFromRegionId: undefined,
                    haulFromRegion: undefined,
                    haulToSiteName: '',
                    haulFromSiteName: '',
                    billingRates: {
                        id: emptyGuid,
                        isActive: true,
                        createdOn: new Date(),
                        lineItemRate: undefined,
                    },
                    isDispatched: false,
                    isInvoiced: false,
                    isCustomPoNumber: false,
                    isCustomerJobNumberCustom: false,
                    isCancelable: true,
                    poNumber: orderReview?.poNumber ?? '',
                    customerJobNumber: orderReview?.customerJobNumber ?? '',
                    siteId: undefined,
                } as OrderReviewLineItemDto;
            }
        },
        [convertQuoteLineItemToOrderReviewLineItem]
    );

    return {
        convertQuoteLineItemToOrderLineItem,
        convertQuoteLineItemToOrderReviewLineItem,
        createNewOrderReviewLineItemFromQuoteLineItem,
    };
}
